import type { AccountRole, BillingInterval, ExchangeProposalSummary, GuildContactRecord, GuildListing, GuildOffering, InvitationPurpose, LeadRecord, MembershipRecord, PlanCode, PropertyDocumentRecord, PropertyFundEntry, PropertyFundRecord, PropertyInvitation, PropertyRecord, PropertyRelationship, QuoteRequestDraft, SessionProfile, WorkspaceContext, WorkspaceContextKind } from '@growth-rings/fmos-schema';
import { supabase } from './supabase';
import { demoLeads, demoProperties } from './demo-data';

const PROFILE_KEY = 'growth-rings-demo-profile';
const PROPERTY_KEY = 'growth-rings-demo-properties';
const LEAD_KEY = 'growth-rings-demo-leads';
const FUND_KEY = 'growth-rings-demo-funds';
const INVITATION_KEY = 'growth-rings-demo-invitations';
const DOCUMENT_KEY = 'growth-rings-demo-documents';
const GUILD_LISTING_KEY = 'growth-rings-demo-guild-listings';
const GUILD_CONTACT_KEY = 'growth-rings-demo-guild-contacts';
const QUOTE_REQUEST_KEY = 'growth-rings-demo-quote-requests';
const WORKSPACE_CONTEXT_KEY = 'growth-rings-workspace-contexts';
const GUILD_OFFERING_KEY = 'growth-rings-guild-offerings';
const EXCHANGE_PROPOSAL_KEY = 'growth-rings-exchange-proposals';

function readLocal<T>(key: string, fallback: T): T {
  try {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) as T : fallback;
  } catch {
    return fallback;
  }
}

function writeLocal<T>(key: string, value: T): void {
  localStorage.setItem(key, JSON.stringify(value));
}

export function getDemoProfile(): SessionProfile | null {
  return readLocal<SessionProfile | null>(PROFILE_KEY, null);
}

export function setDemoProfile(role: AccountRole, email: string): SessionProfile {
  const profile: SessionProfile = {
    id: `demo-${role}`,
    email,
    displayName: role === 'owner' ? 'Alex Property Owner' : role === 'contractor' ? 'RAD Field Team' : role === 'agent' ? 'Morgan Real Estate' : 'Growth Rings Operations',
    role,
    organizationName: role === 'contractor' ? 'RAD Design + Build' : role === 'agent' ? 'Mad River Property Group' : undefined
  };
  writeLocal(PROFILE_KEY, profile);
  return profile;
}

export function clearDemoProfile(): void {
  localStorage.removeItem(PROFILE_KEY);
}

const contextName = (kind: WorkspaceContextKind) => kind === 'owner' ? 'My home + properties' : kind === 'contractor' ? 'My business' : 'My real-estate work';
const mapContext = (row: Record<string, unknown>): WorkspaceContext => ({
  id: String(row.id), profileId: String(row.profile_id), kind: row.kind as WorkspaceContextKind,
  name: String(row.name), isDefault: Boolean(row.is_default), createdAt: String(row.created_at)
});
export async function listWorkspaceContexts(profile: SessionProfile): Promise<WorkspaceContext[]> {
  const initialKind: WorkspaceContextKind = profile.role === 'operator' || profile.role === 'platform_user' ? 'owner' : profile.role;
  const initial: WorkspaceContext = { id:`local-${profile.role}`, profileId:profile.id, kind:initialKind, name:profile.organizationName || contextName(initialKind), isDefault:true, createdAt:new Date().toISOString() };
  if (!supabase) return profile.role === 'platform_user' ? readLocal<WorkspaceContext[]>(WORKSPACE_CONTEXT_KEY,[]) : readLocal<WorkspaceContext[]>(WORKSPACE_CONTEXT_KEY,[initial]);
  if (profile.role === 'operator') return [{...initial,id:'operator-context',name:'Growth Rings operations'}];
  const {data,error}=await supabase.from('user_workspace_contexts').select('*').order('created_at',{ascending:true});
  if(error) throw error;
  return (data ?? []).map(row=>mapContext(row as Record<string,unknown>));
}
export async function createWorkspaceContext(profile: SessionProfile,kind:WorkspaceContextKind,name:string):Promise<WorkspaceContext> {
  const clean=name.trim() || contextName(kind);
  if(!supabase) {
    const existing=await listWorkspaceContexts(profile);
    if(existing.some(item=>item.kind===kind)) throw new Error('That workspace is already available.');
    const created:WorkspaceContext={id:`local-${kind}`,profileId:profile.id,kind,name:clean,isDefault:false,createdAt:new Date().toISOString()};
    writeLocal(WORKSPACE_CONTEXT_KEY,[...existing,created]);return created;
  }
  const {data,error}=await supabase.from('user_workspace_contexts').insert({profile_id:profile.id,kind,name:clean}).select('*').single();
  if(error) throw error;
  return mapContext(data as Record<string,unknown>);
}


export async function updateWorkspaceContext(profile: SessionProfile,id:string,name:string):Promise<WorkspaceContext> {
  const clean=name.trim();
  if(!clean || clean.length>120) throw new Error('Enter a workspace name of 1 to 120 characters.');
  if(profile.role==='operator') throw new Error('Operator workspace settings are managed administratively.');
  if(!supabase) {
    const existing=await listWorkspaceContexts(profile);
    const current=existing.find(item=>item.id===id);
    if(!current) throw new Error('Workspace not found.');
    const updated={...current,name:clean};
    writeLocal(WORKSPACE_CONTEXT_KEY,existing.map(item=>item.id===id?updated:item));
    return updated;
  }
  const {data,error}=await supabase.from('user_workspace_contexts').update({name:clean}).eq('id',id).eq('profile_id',profile.id).select('*').single();
  if(error) throw error;
  return mapContext(data as Record<string,unknown>);
}

export async function getLiveProfile(): Promise<SessionProfile | null> {
  if (!supabase) return getDemoProfile();
  const { data: authData } = await supabase.auth.getUser();
  if (!authData.user) return null;
  const { data, error } = await supabase.from('profiles').select('*').eq('id', authData.user.id).maybeSingle();
  if (error) throw error;
  if (!data?.primary_role) return null;
  return {
    id: data.id,
    email: data.email ?? authData.user.email ?? '',
    displayName: data.display_name ?? authData.user.email?.split('@')[0] ?? 'Growth Rings member',
    role: data.primary_role,
    organizationName: data.organization_name ?? undefined
  };
}

export async function saveLiveProfile(role: AccountRole, email: string, displayName?: string): Promise<SessionProfile> {
  if (!supabase) return setDemoProfile(role, email);
  if (role === 'operator') throw new Error('Operator access must be granted administratively.');
  const { data: authData, error: authError } = await supabase.auth.getUser();
  if (authError || !authData.user) throw authError ?? new Error('Please sign in first.');
  const resolvedDisplayName = displayName?.trim() || email.split('@')[0];
  const { data, error } = await supabase.rpc('save_my_profile', {
    p_role: role,
    p_display_name: resolvedDisplayName
  });
  if (error) throw error;
  const saved = data as {
    id: string;
    email: string | null;
    display_name: string | null;
    primary_role: AccountRole;
    organization_name: string | null;
  };
  return {
    id: saved.id,
    email: saved.email ?? authData.user.email ?? email,
    displayName: saved.display_name ?? resolvedDisplayName,
    role: saved.primary_role,
    organizationName: saved.organization_name ?? undefined
  };
}

export async function listProperties(): Promise<PropertyRecord[]> {
  if (!supabase) return readLocal(PROPERTY_KEY, demoProperties);
  const { data, error } = await supabase.from('properties').select('*').is('archived_at', null).order('updated_at', { ascending: false });
  if (error) throw error;
  return (data ?? []).map((row) => ({
    id: row.id,
    name: row.name,
    address: row.address ?? '',
    municipality: row.municipality ?? '',
    region: row.region ?? '',
    coordinates: row.latitude != null && row.longitude != null ? { latitude: row.latitude, longitude: row.longitude } : undefined,
    parcelId: row.parcel_id ?? undefined,
    stage: row.stage,
    readiness: row.readiness ?? 0,
    openDecisions: row.open_decisions ?? 0,
    activeProjectCount: row.active_project_count ?? 0,
    updatedAt: row.updated_at
  }));
}

export async function createProperty(input: Omit<PropertyRecord, 'id' | 'readiness' | 'openDecisions' | 'activeProjectCount' | 'updatedAt'>): Promise<PropertyRecord> {
  if (!supabase) {
    const property: PropertyRecord = {
      ...input,
      id: crypto.randomUUID(),
      readiness: input.coordinates ? 36 : 22,
      openDecisions: 1,
      activeProjectCount: 0,
      updatedAt: new Date().toISOString()
    };
    const next = [property, ...readLocal(PROPERTY_KEY, demoProperties)];
    writeLocal(PROPERTY_KEY, next);
    return property;
  }

  const { data, error } = await supabase.rpc('create_property_with_owner', {
    p_name: input.name,
    p_address: input.address,
    p_municipality: input.municipality,
    p_region: input.region,
    p_stage: input.stage,
    p_latitude: input.coordinates?.latitude ?? null,
    p_longitude: input.coordinates?.longitude ?? null
  });
  if (error) throw error;
  const property = data as PropertyRecord & { eventId?: string };
  if (!property?.id || !property.name) {
    throw new Error('Supabase returned an unexpected response after creating the property. Refresh the portfolio before trying again.');
  }
  if (property.eventId) {
    try {
      const dispatch = await supabase.functions.invoke('dispatch-event', { body: { eventId: property.eventId } });
      if (dispatch.error) console.warn('Property saved; enrichment dispatch is awaiting retry.', dispatch.error);
    } catch (dispatchError) {
      console.warn('Property saved; enrichment dispatch is awaiting retry.', dispatchError);
    }
  }
  return property;
}

export async function updatePropertyCoordinates(propertyId: string, latitude: number, longitude: number): Promise<void> {
  if (!supabase) {
    const next = readLocal<PropertyRecord[]>(PROPERTY_KEY, demoProperties).map((property) => property.id === propertyId
      ? { ...property, coordinates: { latitude, longitude }, updatedAt: new Date().toISOString() }
      : property);
    writeLocal(PROPERTY_KEY, next);
    return;
  }
  const { error } = await supabase.from('properties').update({ latitude, longitude }).eq('id', propertyId);
  if (error) throw error;
}

export async function updatePropertyDetails(property: Pick<PropertyRecord,'id'|'name'|'address'|'municipality'|'region'|'stage'>):Promise<PropertyRecord> {
  if(!property.name.trim())throw new Error('Property name is required.');
  if(!supabase) {
    const rows=readLocal<PropertyRecord[]>(PROPERTY_KEY,demoProperties);
    const current=rows.find(item=>item.id===property.id);if(!current)throw new Error('Property record not found.');
    const updated={...current,...property,name:property.name.trim(),updatedAt:new Date().toISOString()};
    writeLocal(PROPERTY_KEY,rows.map(item=>item.id===property.id?updated:item));return updated;
  }
  const {data,error}=await supabase.from('properties').update({name:property.name.trim(),address:property.address.trim(),municipality:property.municipality.trim(),region:property.region.trim(),stage:property.stage}).eq('id',property.id).select('*').single();
  if(error)throw error;
  return {id:data.id,name:data.name,address:data.address??'',municipality:data.municipality??'',region:data.region??'',coordinates:data.latitude!=null&&data.longitude!=null?{latitude:data.latitude,longitude:data.longitude}:undefined,parcelId:data.parcel_id??undefined,stage:data.stage,readiness:data.readiness??0,openDecisions:data.open_decisions??0,activeProjectCount:data.active_project_count??0,updatedAt:data.updated_at};
}

export async function listLeads(): Promise<LeadRecord[]> {
  if (!supabase) return readLocal(LEAD_KEY, demoLeads);
  const { data, error } = await supabase.from('leads').select('*').is('archived_at', null).order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []).map((row) => ({
    id: row.id,
    propertyId: row.property_id,
    projectId: row.project_id,
    title: row.title,
    location: row.location,
    trade: row.trade,
    fitScore: row.fit_score,
    status: row.status,
    clientPermission: row.client_permission,
    createdAt: row.created_at
  }));
}

export async function updateLeadStatus(id: string, status: LeadRecord['status']): Promise<void> {
  if (!supabase) {
    const next = readLocal(LEAD_KEY, demoLeads).map((lead) => lead.id === id ? { ...lead, status } : lead);
    writeLocal(LEAD_KEY, next);
    return;
  }
  const { error } = await supabase.from('leads').update({ status }).eq('id', id);
  if (error) throw error;
}

export async function updateLeadPriority(id: string, fitScore: number): Promise<void> {
  const score = Math.max(0, Math.min(100, Math.round(fitScore)));
  if (!supabase) {
    const next = readLocal(LEAD_KEY, demoLeads).map((lead) => lead.id === id ? { ...lead, fitScore: score } : lead);
    writeLocal(LEAD_KEY, next);
    return;
  }
  const { error } = await supabase.from('leads').update({ fit_score: score }).eq('id', id);
  if (error) throw error;
}

export async function createProjectLead(input: {
  propertyId: string;
  title: string;
  description: string;
  trade: string;
  timeline: string;
  budgetBand: string;
  clientPermission: boolean;
  sourceChannel?: 'member_request'|'guild_search'|'direct_customer'|'referral'|'stripe_network'|'wix_import'|'shopify_import'|'other';
  sourceDetail?: string;
  sourceProfileId?: string;
  sourceMetadata?: Record<string, unknown>;
}): Promise<LeadRecord> {
  if (!supabase) {
    const property = readLocal(PROPERTY_KEY, demoProperties).find((item) => item.id === input.propertyId);
    const lead: LeadRecord = {
      id: crypto.randomUUID(), propertyId: input.propertyId, projectId: crypto.randomUUID(),
      title: input.title, location: property?.address || 'Location pending', trade: input.trade,
      fitScore: 50, status: 'qualified',
      clientPermission: input.clientPermission, createdAt: new Date().toISOString()
    };
    writeLocal(LEAD_KEY, [lead, ...readLocal(LEAD_KEY, demoLeads)]);
    return lead;
  }
  const { data, error } = await supabase.rpc('create_project_lead', {
    p_property_id: input.propertyId,
    p_title: input.title,
    p_description: input.description,
    p_trade: input.trade,
    p_timeline: input.timeline,
    p_budget_band: input.budgetBand,
    p_client_permission: input.clientPermission,
    p_source_channel: input.sourceChannel ?? 'member_request',
    p_source_detail: input.sourceDetail ?? null,
    p_source_profile_id: input.sourceProfileId ?? null,
    p_source_metadata: input.sourceMetadata ?? {}
  });
  if (error) throw error;
  const result = data as { lead: LeadRecord; eventId: string };
  const dispatch = await supabase.functions.invoke('dispatch-event', { body: { eventId: result.eventId } });
  if (dispatch.error) console.warn('Lead saved; automation dispatch is awaiting retry.', dispatch.error);
  return result.lead;
}

export async function getMembership(propertyId?: string): Promise<MembershipRecord> {
  if (!supabase) return {
    planCode: 'pilot', status: 'active', propertyId, cancelAtPeriodEnd: false
  };
  const { data, error } = await supabase.rpc('get_my_membership', {
    target_property: propertyId ?? null
  });
  if (error) throw error;
  const membership = data as MembershipRecord;
  if (membership.stripeCustomerId) return membership;
  const { data: customer, error: customerError } = await supabase
    .from('billing_customers')
    .select('stripe_customer_id')
    .maybeSingle();
  if (customerError) throw customerError;
  return customer?.stripe_customer_id ? { ...membership, stripeCustomerId: customer.stripe_customer_id } : membership;
}

function demoFund(propertyId: string): PropertyFundRecord {
  return {
    id: `demo-fund-${propertyId}`,
    propertyId,
    name: 'Property Ring fund',
    currency: 'usd',
    trackedBalanceCents: 184200,
    sources: [
      { id: 'source-owner', propertyId, label: 'Owner contribution', sourceType: 'owner', status: 'connected', cadence: 'Monthly', amountCents: 25000 },
      { id: 'source-payroll', propertyId, label: 'Payroll direct deposit · Bank XYZ', sourceType: 'payroll', status: 'planned', cadence: 'Each pay period', amountCents: 10000 },
      { id: 'source-grant', propertyId, label: 'Redevelopment grant', sourceType: 'grant', status: 'planned' }
    ],
    allocations: [
      { id: 'allocation-site', propertyId, label: 'Site + septic planning', committedCents: 52000 },
      { id: 'allocation-barn', propertyId, label: 'Post-and-beam barn scope', committedCents: 35000 },
      { id: 'allocation-reserve', propertyId, label: 'Stewardship reserve', committedCents: 24000 }
    ],
    entries: [
      { id: 'entry-1', propertyId, label: 'Owner contribution', amountCents: 25000, direction: 'in', status: 'recorded', category: 'owner', occurredAt: new Date().toISOString() },
      { id: 'entry-2', propertyId, label: 'Septic design allowance', amountCents: 7500, direction: 'out', status: 'planned', category: 'project', occurredAt: new Date().toISOString() }
    ]
  };
}

export async function getPropertyFund(propertyId: string): Promise<PropertyFundRecord> {
  if (!supabase) {
    const funds = readLocal<PropertyFundRecord[]>(FUND_KEY, []);
    return funds.find((fund) => fund.propertyId === propertyId) ?? demoFund(propertyId);
  }
  const { data: existingFund, error } = await supabase.from('property_funds').select('*').eq('property_id', propertyId).maybeSingle();
  if (error) throw error;
  let fund = existingFund;
  if (!fund) {
    const { data: created, error: createError } = await supabase.from('property_funds').insert({ property_id: propertyId, name: 'Property Ring fund' }).select('*').single();
    if (createError) throw createError;
    fund = created;
  }
  const [{ data: sources, error: sourcesError }, { data: allocations, error: allocationsError }, { data: entries, error: entriesError }] = await Promise.all([
    supabase.from('property_fund_sources').select('*').eq('property_id', propertyId).is('archived_at', null).order('created_at'),
    supabase.from('property_fund_allocations').select('*').eq('property_id', propertyId).is('archived_at', null).order('created_at'),
    supabase.from('property_fund_entries').select('*').eq('property_id', propertyId).is('archived_at', null).order('occurred_at', { ascending: false })
  ]);
  if (sourcesError) throw sourcesError;
  if (allocationsError) throw allocationsError;
  if (entriesError) throw entriesError;
  return {
    id: fund.id, propertyId, name: fund.name, currency: fund.currency,
    trackedBalanceCents: fund.tracked_balance_cents,
    sources: (sources ?? []).map((row) => ({ id: row.id, propertyId: row.property_id, label: row.label, sourceType: row.source_type, status: row.status, cadence: row.cadence ?? undefined, amountCents: row.amount_cents ?? undefined })),
    allocations: (allocations ?? []).map((row) => ({ id: row.id, propertyId: row.property_id, label: row.label, committedCents: row.committed_cents })),
    entries: (entries ?? []).map((row) => ({ id: row.id, propertyId: row.property_id, label: row.label, amountCents: row.amount_cents, direction: row.direction, status: row.status, category: row.category, occurredAt: row.occurred_at }))
  };
}

export async function addPlannedFundEntry(propertyId: string, label: string, amountCents: number, direction: 'in' | 'out'): Promise<PropertyFundEntry> {
  const entry: PropertyFundEntry = { id: crypto.randomUUID(), propertyId, label, amountCents, direction, status: 'planned', category: 'planning', occurredAt: new Date().toISOString() };
  if (!supabase) {
    const funds = readLocal<PropertyFundRecord[]>(FUND_KEY, []);
    const current = funds.find((fund) => fund.propertyId === propertyId) ?? demoFund(propertyId);
    const next = { ...current, entries: [entry, ...current.entries] };
    writeLocal(FUND_KEY, [next, ...funds.filter((fund) => fund.propertyId !== propertyId)]);
    return entry;
  }
  const { data, error } = await supabase.from('property_fund_entries').insert({ property_id: propertyId, label, amount_cents: amountCents, direction, status: 'planned', category: 'planning' }).select('*').single();
  if (error) throw error;
  return { id: data.id, propertyId: data.property_id, label: data.label, amountCents: data.amount_cents, direction: data.direction, status: data.status, category: data.category, occurredAt: data.occurred_at };
}

export async function listPropertyInvitations(propertyId: string): Promise<PropertyInvitation[]> {
  if (!supabase) return readLocal<PropertyInvitation[]>(INVITATION_KEY, []).filter((invite) => invite.propertyId === propertyId);
  const { data, error } = await supabase.from('property_invitations').select('*').eq('property_id', propertyId).is('archived_at', null).order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []).map((row) => ({ id: row.id, propertyId: row.property_id, email: row.email, relationship: row.relationship, purpose: row.purpose, relationshipDetail: row.relationship_detail ?? undefined, purposeDetail: row.purpose_detail ?? undefined, status: row.status, createdAt: row.created_at }));
}

export async function createPropertyInvitation(input: { propertyId: string; email: string; relationship: PropertyRelationship; purpose: InvitationPurpose; relationshipDetail?: string; purposeDetail?: string }): Promise<PropertyInvitation> {
  if (!supabase) {
    const invite: PropertyInvitation = { id: crypto.randomUUID(), ...input, status: 'draft', createdAt: new Date().toISOString() };
    writeLocal(INVITATION_KEY, [invite, ...readLocal<PropertyInvitation[]>(INVITATION_KEY, [])]);
    return invite;
  }
  const { data, error } = await supabase.from('property_invitations').insert({ property_id: input.propertyId, email: input.email, relationship: input.relationship, purpose: input.purpose, relationship_detail: input.relationshipDetail ?? null, purpose_detail: input.purposeDetail ?? null, status: 'draft' }).select('*').single();
  if (error) throw error;
  return { id: data.id, propertyId: data.property_id, email: data.email, relationship: data.relationship, purpose: data.purpose, relationshipDetail: data.relationship_detail ?? undefined, purposeDetail: data.purpose_detail ?? undefined, status: data.status, createdAt: data.created_at };
}

export async function sendPropertyInvitationEmail(invitationId: string): Promise<void> {
  if (!supabase) throw new Error('Connect Supabase before sending an invitation email.');
  const { error } = await supabase.functions.invoke('send-property-invitation', { body: { invitationId } });
  if (error) throw new Error(await edgeFunctionMessage(error, 'Invitation email could not be sent.'));
}

export async function listPropertyDocuments(propertyId: string): Promise<PropertyDocumentRecord[]> {
  if (!supabase) return readLocal<PropertyDocumentRecord[]>(DOCUMENT_KEY, []).filter((document) => document.propertyId === propertyId);
  const { data, error } = await supabase.from('documents').select('id,property_id,title,category,visibility,created_at').eq('property_id', propertyId).is('archived_at', null).order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []).map((row) => ({ id: row.id, propertyId: row.property_id, title: row.title, category: row.category, visibility: row.visibility, createdAt: row.created_at }));
}

export async function uploadPropertyDocument(propertyId: string, file: File, category: string, visibility: PropertyDocumentRecord['visibility']): Promise<PropertyDocumentRecord> {
  if (!supabase) {
    const document: PropertyDocumentRecord = { id: crypto.randomUUID(), propertyId, title: file.name, category, visibility, createdAt: new Date().toISOString() };
    writeLocal(DOCUMENT_KEY, [document, ...readLocal<PropertyDocumentRecord[]>(DOCUMENT_KEY, [])]);
    return document;
  }
  const { data: authData, error: authError } = await supabase.auth.getUser();
  if (authError || !authData.user) throw authError ?? new Error('Please sign in first.');
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '-');
  const storagePath = `${propertyId}/${crypto.randomUUID()}-${safeName}`;
  const { error: uploadError } = await supabase.storage.from('property-documents').upload(storagePath, file, { upsert: false });
  if (uploadError) throw uploadError;
  const { data, error } = await supabase.from('documents').insert({ property_id: propertyId, uploaded_by: authData.user.id, title: file.name, category, storage_path: storagePath, visibility }).select('*').single();
  if (error) throw error;
  return { id: data.id, propertyId: data.property_id, title: data.title, category: data.category, visibility: data.visibility, createdAt: data.created_at };
}

export async function getPropertyDocumentUrl(documentId: string): Promise<string> {
  if (!supabase) throw new Error('Document files are available after the property database is connected.');
  const { data: document, error } = await supabase.from('documents').select('storage_path').eq('id', documentId).single();
  if (error) throw error;
  const { data, error: signedError } = await supabase.storage.from('property-documents').createSignedUrl(document.storage_path, 300);
  if (signedError) throw signedError;
  return data.signedUrl;
}

const demoGuildListings: GuildListing[] = [
  {
    profileId: 'guild-demo-builder', businessName: 'Timberline Design + Build',
    headline: 'Practical building support from site walk through closeout.',
    biography: 'Pilot directory example for residential additions, outbuildings and phased property improvements.',
    trades: ['Design + build', 'Carpentry + structural repair'], serviceAreas: ['Central Vermont'],
    websiteUrl: 'https://www.growthringsproperty.com', inquiryEmail: 'builder@example.com',
    memberOffer: 'Complimentary 20-minute project-fit conversation', discountCode: 'GROWTHRINGS',
    status: 'active', verification: 'listed', isPublic: true, updatedAt: new Date().toISOString()
  },
  {
    profileId: 'guild-demo-site', businessName: 'Green Mountain Site Works',
    headline: 'Access, drainage and small-site preparation.',
    biography: 'Pilot directory example showing how a field-service shingle will appear.',
    trades: ['Excavation + site work', 'Septic + wastewater'], serviceAreas: ['Washington County', 'Lamoille County'],
    inquiryEmail: 'sitework@example.com', memberOffer: 'Growth Rings members receive a documented site-walk summary.',
    status: 'active', verification: 'self_reported', isPublic: true, updatedAt: new Date().toISOString()
  }
];

function mapGuildListing(row: Record<string, unknown>): GuildListing {
  return {
    profileId: String(row.profile_id), businessName: String(row.business_name ?? row.headline ?? 'Guild member'),
    headline: String(row.headline ?? ''), biography: String(row.biography ?? ''),
    trades: (row.trades as string[] | null) ?? [], serviceAreas: (row.service_areas as string[] | null) ?? [],
    websiteUrl: row.website_url ? String(row.website_url) : undefined,
    inquiryEmail: String(row.inquiry_email ?? ''), phone: row.phone ? String(row.phone) : undefined,
    memberOffer: row.member_offer ? String(row.member_offer) : undefined,
    discountCode: row.discount_code ? String(row.discount_code) : undefined,
    logoUrl: row.logo_url ? String(row.logo_url) : undefined,
    listingManagerName: row.listing_manager_name ? String(row.listing_manager_name) : undefined,
    listingManagerEmail: row.listing_manager_email ? String(row.listing_manager_email) : undefined,
    status: (row.listing_status as GuildListing['status']) ?? 'draft',
    verification: (row.verification as GuildListing['verification']) ?? 'listed',
    isPublic: Boolean(row.is_public), updatedAt: String(row.updated_at ?? new Date().toISOString())
  };
}

export async function listGuildListings(): Promise<GuildListing[]> {
  if (!supabase) return readLocal<GuildListing[]>(GUILD_LISTING_KEY, demoGuildListings).filter((listing) => listing.isPublic && listing.status === 'active');
  const { data, error } = await supabase.from('professional_profiles').select('*').eq('is_public', true).eq('listing_status', 'active').order('updated_at', { ascending: false });
  if (error) throw error;
  return (data ?? []).map((row) => mapGuildListing(row));
}

export async function getMyGuildListing(profileId: string): Promise<GuildListing | null> {
  if (!supabase) return readLocal<GuildListing[]>(GUILD_LISTING_KEY, demoGuildListings).find((listing) => listing.profileId === profileId) ?? null;
  const { data, error } = await supabase.from('professional_profiles').select('*').eq('profile_id', profileId).maybeSingle();
  if (error) throw error;
  return data ? mapGuildListing(data) : null;
}

export async function saveGuildListing(input: Omit<GuildListing, 'profileId' | 'updatedAt' | 'verification'>): Promise<GuildListing> {
  if (!supabase) {
    const profile = getDemoProfile();
    if (!profile) throw new Error('Open an account before publishing a Guild shingle.');
    const existing = readLocal<GuildListing[]>(GUILD_LISTING_KEY, demoGuildListings);
    const listing: GuildListing = { ...input, profileId: profile.id, verification: 'self_reported', updatedAt: new Date().toISOString() };
    writeLocal(GUILD_LISTING_KEY, [listing, ...existing.filter((item) => item.profileId !== profile.id)]);
    return listing;
  }
  const { data: authData, error: authError } = await supabase.auth.getUser();
  if (authError || !authData.user) throw authError ?? new Error('Please sign in first.');
  const { data, error } = await supabase.from('professional_profiles').upsert({
    profile_id: authData.user.id,
    business_name: input.businessName,
    headline: input.headline,
    biography: input.biography,
    trades: input.trades,
    service_areas: input.serviceAreas,
    website_url: input.websiteUrl ?? null,
    inquiry_email: input.inquiryEmail,
    phone: input.phone ?? null,
    member_offer: input.memberOffer ?? null,
    discount_code: input.discountCode ?? null,
    logo_url: input.logoUrl ?? null,
    listing_manager_name: input.listingManagerName ?? null,
    listing_manager_email: input.listingManagerEmail ?? null,
    listing_status: input.status,
    verification: 'self_reported',
    is_public: input.isPublic
  }, { onConflict: 'profile_id' }).select('*').single();
  if (error) throw error;
  return mapGuildListing(data);
}

export async function listGuildContacts(propertyId: string): Promise<GuildContactRecord[]> {
  if (!supabase) return readLocal<GuildContactRecord[]>(GUILD_CONTACT_KEY, []).filter((item) => item.propertyId === propertyId);
  const { data, error } = await supabase.from('property_guild_contacts').select('*').eq('property_id', propertyId).order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []).map((row) => ({ id: row.id, propertyId: row.property_id, professionalProfileId: row.professional_profile_id, createdAt: row.created_at }));
}

export async function saveGuildContact(propertyId: string, professionalProfileId: string): Promise<GuildContactRecord> {
  if (!supabase) {
    const existing = readLocal<GuildContactRecord[]>(GUILD_CONTACT_KEY, []);
    const found = existing.find((item) => item.propertyId === propertyId && item.professionalProfileId === professionalProfileId);
    if (found) return found;
    const contact = { id: crypto.randomUUID(), propertyId, professionalProfileId, createdAt: new Date().toISOString() };
    writeLocal(GUILD_CONTACT_KEY, [contact, ...existing]);
    return contact;
  }
  const { data, error } = await supabase.from('property_guild_contacts').upsert({ property_id: propertyId, professional_profile_id: professionalProfileId }, { onConflict: 'property_id,professional_profile_id' }).select('*').single();
  if (error) throw error;
  return { id: data.id, propertyId: data.property_id, professionalProfileId: data.professional_profile_id, createdAt: data.created_at };
}

export async function listQuoteRequestDrafts(propertyId: string): Promise<QuoteRequestDraft[]> {
  if (!supabase) return readLocal<QuoteRequestDraft[]>(QUOTE_REQUEST_KEY, []).filter((item) => item.propertyId === propertyId);
  const { data, error } = await supabase.from('project_quote_requests').select('*,professional_profiles!project_quote_requests_professional_profile_id_fkey(business_name)').eq('property_id', propertyId).order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []).map((row) => ({
    id: row.id, propertyId: row.property_id, projectId: row.project_id,
    professionalProfileId: row.professional_profile_id,
    businessName: row.professional_profiles?.business_name ?? 'Guild member',
    message: row.message ?? '', status: row.status, createdAt: row.created_at
  }));
}

export async function createQuoteRequestDraft(input: { propertyId: string; projectId: string; professionalProfileId: string; businessName: string; message: string }): Promise<QuoteRequestDraft> {
  if (!supabase) {
    const draft: QuoteRequestDraft = { id: crypto.randomUUID(), ...input, status: 'draft', createdAt: new Date().toISOString() };
    writeLocal(QUOTE_REQUEST_KEY, [draft, ...readLocal<QuoteRequestDraft[]>(QUOTE_REQUEST_KEY, [])]);
    return draft;
  }
  const { data, error } = await supabase.from('project_quote_requests').insert({
    property_id: input.propertyId,
    project_id: input.projectId,
    professional_profile_id: input.professionalProfileId,
    message: input.message,
    status: 'draft'
  }).select('*').single();
  if (error) throw error;
  return { id: data.id, propertyId: data.property_id, projectId: data.project_id, professionalProfileId: data.professional_profile_id, businessName: input.businessName, message: data.message ?? '', status: data.status, createdAt: data.created_at };
}

export async function startMembershipCheckout(planCode: Exclude<PlanCode, 'pilot'>, interval: BillingInterval, propertyId?: string): Promise<void> {
  if (!supabase) throw new Error('Connect Supabase before opening Stripe Checkout.');
  const { data, error } = await supabase.functions.invoke('create-checkout-session', {
    body: { planCode, interval, propertyId: propertyId ?? null }
  });
  if (error) throw new Error(await edgeFunctionMessage(error, 'Checkout could not be opened.'));
  const url = (data as { url?: string })?.url;
  if (!url) throw new Error('Stripe Checkout did not return a destination.');
  window.location.assign(url);
}

export async function openBillingPortal(): Promise<void> {
  if (!supabase) throw new Error('Connect Supabase before opening billing.');
  const { data, error } = await supabase.functions.invoke('create-billing-portal', { body: {} });
  if (error) throw new Error(await edgeFunctionMessage(error, 'Billing could not be opened.'));
  const url = (data as { url?: string })?.url;
  if (!url) throw new Error('The billing portal did not return a destination.');
  window.location.assign(url);
}

export async function edgeFunctionMessage(error: unknown, fallback: string): Promise<string> {
  const context = (error as { context?: Response })?.context;
  if (context) {
    try {
      const payload = await context.clone().json() as { error?: string; message?: string };
      if (payload.error) return payload.error;
      if (payload.message) return payload.message;
    } catch {
      try {
        const text = await context.clone().text();
        if (text.trim()) return text.trim();
      } catch {
        // Fall through to the SDK message.
      }
    }
  }
  return error instanceof Error && error.message !== 'Edge Function returned a non-2xx status code' ? error.message : fallback;
}


function mapGuildOffering(row: Record<string, unknown>): GuildOffering {
  return {
    id: String(row.id),
    ownerProfileId: String(row.owner_profile_id),
    sku: String(row.sku),
    title: String(row.title),
    description: row.description ? String(row.description) : undefined,
    offeringKind: row.offering_kind as GuildOffering['offeringKind'],
    unitLabel: String(row.unit_label ?? 'each'),
    availableQuantity: row.available_quantity == null ? undefined : Number(row.available_quantity),
    indicativeValueCents: row.indicative_value_minor == null ? undefined : Number(row.indicative_value_minor),
    currency: String(row.currency ?? 'usd'),
    barterEnabled: Boolean(row.barter_enabled),
    cashEnabled: Boolean(row.cash_enabled),
    visibility: row.visibility as GuildOffering['visibility'],
    status: row.status as GuildOffering['status'],
    updatedAt: String(row.updated_at ?? new Date().toISOString())
  };
}

export async function listGuildOfferings(ownerProfileId?: string): Promise<GuildOffering[]> {
  if (!supabase) {
    const all = readLocal<GuildOffering[]>(GUILD_OFFERING_KEY, []);
    return ownerProfileId ? all.filter((item) => item.ownerProfileId === ownerProfileId) : all.filter((item) => item.status === 'active' && item.visibility === 'public');
  }
  let query = supabase.from('guild_offerings').select('*').order('updated_at', { ascending: false });
  if (ownerProfileId) query = query.eq('owner_profile_id', ownerProfileId);
  else query = query.eq('status', 'active').eq('visibility', 'public');
  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []).map((row) => mapGuildOffering(row));
}

export async function saveGuildOffering(input: Omit<GuildOffering, 'id' | 'ownerProfileId' | 'updatedAt'>): Promise<GuildOffering> {
  if (!supabase) {
    const profile = getDemoProfile();
    if (!profile) throw new Error('Open an account before adding a Guild offering.');
    const existing = readLocal<GuildOffering[]>(GUILD_OFFERING_KEY, []);
    const prior = existing.find((item) => item.ownerProfileId === profile.id && item.sku === input.sku);
    const offering: GuildOffering = {
      ...input,
      id: prior?.id ?? crypto.randomUUID(),
      ownerProfileId: profile.id,
      updatedAt: new Date().toISOString()
    };
    writeLocal(GUILD_OFFERING_KEY, [offering, ...existing.filter((item) => item.id !== offering.id)]);
    return offering;
  }
  const { data: authData, error: authError } = await supabase.auth.getUser();
  if (authError || !authData.user) throw authError ?? new Error('Please sign in first.');
  const { data, error } = await supabase.from('guild_offerings').upsert({
    owner_profile_id: authData.user.id,
    sku: input.sku.trim().toUpperCase(),
    title: input.title,
    description: input.description ?? null,
    offering_kind: input.offeringKind,
    unit_label: input.unitLabel,
    available_quantity: input.availableQuantity ?? null,
    indicative_value_minor: input.indicativeValueCents ?? null,
    currency: input.currency,
    barter_enabled: input.barterEnabled,
    cash_enabled: input.cashEnabled,
    visibility: input.visibility,
    status: input.status
  }, { onConflict: 'owner_profile_id,sku' }).select('*').single();
  if (error) throw error;
  return mapGuildOffering(data);
}

export async function createGuildExchangeProposal(input: {
  counterpartyProfileId: string;
  title: string;
  message?: string;
  offeredOfferingId: string;
  offeredQuantity: number;
  requestedOfferingId: string;
  requestedQuantity: number;
  propertyId?: string;
  projectId?: string;
}): Promise<ExchangeProposalSummary> {
  if (!supabase) {
    const profile = getDemoProfile();
    if (!profile) throw new Error('Open an account before proposing an exchange.');
    const proposal: ExchangeProposalSummary = {
      id: crypto.randomUUID(),
      initiatorProfileId: profile.id,
      counterpartyProfileId: input.counterpartyProfileId,
      title: input.title,
      message: input.message,
      status: 'proposed',
      createdAt: new Date().toISOString()
    };
    writeLocal(EXCHANGE_PROPOSAL_KEY, [proposal, ...readLocal<ExchangeProposalSummary[]>(EXCHANGE_PROPOSAL_KEY, [])]);
    return proposal;
  }
  const { data, error } = await supabase.rpc('create_exchange_proposal', {
    p_counterparty_profile_id: input.counterpartyProfileId,
    p_title: input.title,
    p_message: input.message ?? null,
    p_offered_offering_id: input.offeredOfferingId,
    p_offered_quantity: input.offeredQuantity,
    p_requested_offering_id: input.requestedOfferingId,
    p_requested_quantity: input.requestedQuantity,
    p_property_id: input.propertyId ?? null,
    p_project_id: input.projectId ?? null
  });
  if (error) throw error;
  const result = data as { id: string; status: ExchangeProposalSummary['status']; createdAt: string };
  const { data: authData } = await supabase.auth.getUser();
  return {
    id: result.id,
    initiatorProfileId: authData.user?.id ?? '',
    counterpartyProfileId: input.counterpartyProfileId,
    title: input.title,
    message: input.message,
    status: result.status,
    createdAt: result.createdAt
  };
}

export async function listMyExchangeProposals(): Promise<ExchangeProposalSummary[]> {
  if (!supabase) return readLocal<ExchangeProposalSummary[]>(EXCHANGE_PROPOSAL_KEY, []);
  const { data, error } = await supabase.from('exchange_proposals').select('*').order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []).map((row) => ({
    id: row.id,
    initiatorProfileId: row.initiator_profile_id,
    counterpartyProfileId: row.counterparty_profile_id,
    title: row.title,
    message: row.message ?? undefined,
    status: row.status,
    createdAt: row.created_at
  }));
}

export async function acceptGuildExchange(proposalId: string): Promise<void> {
  if (!supabase) return;
  const { error } = await supabase.rpc('accept_exchange', { p_proposal_id: proposalId });
  if (error) throw error;
}
